import { useId, useState, type MouseEvent } from "react";
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
} from "@mui/material";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { motion } from "motion/react";
import { getStatusConfig, getMenuItems } from "./findingStatusConfig";
import type { FindingStatusIndicatorProps, MenuCallbacks } from "./types";

export function FindingStatusIndicator({
  states,
  findingName,
  onFixFinding,
  onCreateTicket,
  onSetException,
  onViewFix,
  onViewTicket,
  onViewException,
  onCopyLink,
  onViewDetails,
  canCreateTicket,
  canSetException,
}: FindingStatusIndicatorProps) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hovered, setHovered] = useState(false);
  const menuId = useId();

  const {
    label,
    icon: Icon,
    backgroundColor,
    color,
  } = getStatusConfig(states.overall);
  const resolvedBg = backgroundColor(theme);
  const resolvedColor = color(theme);

  const callbacks: MenuCallbacks = {
    onFixFinding,
    onCreateTicket,
    onSetException,
    onViewFix,
    onViewTicket,
    onViewException,
    onCopyLink,
    onViewDetails,
    canCreateTicket,
    canSetException,
  };

  const menuItems = getMenuItems(states, callbacks);
  const open = Boolean(anchorEl);
  const showChevron = hovered || open;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        component="button"
        data-testid="finding-status-indicator"
        data-status={states.overall}
        aria-label={`${label} status, open actions menu`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={handleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          height: 20,
          px: "8px",
          borderRadius: "8px",
          border: "none",
          cursor: "pointer",
          backgroundColor: resolvedBg,
          color: resolvedColor,
          "&:hover": { opacity: 0.85 },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: 1,
          },
        }}
      >
        <Icon sx={{ fontSize: 12 }} aria-hidden />
        <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1 }}>
          {label}
        </Typography>
        <motion.span
          animate={{
            width: showChevron ? "auto" : 0,
            opacity: showChevron ? 1 : 0,
          }}
          initial={false}
          transition={{ type: "spring", stiffness: 800, damping: 30 }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            overflow: "hidden",
          }}
          aria-hidden
        >
          <ChevronRightIcon sx={{ fontSize: 16 }} />
        </motion.span>
      </Box>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: { sx: { py: 1.5, minWidth: 180, maxWidth: 180 } },
          list: { sx: { py: 0 } },
        }}
      >
        {findingName && (
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ px: 2, pt: 0.5, pb: 0.5, maxWidth: 250 }}
          >
            {findingName}
          </Typography>
        )}
        {menuItems.map((item) => [
          item.dividerBefore && (
            <Divider key={`divider-${item.key}`} sx={{ mx: 1.5 }} />
          ),
          <MenuItem
            key={item.key}
            disabled={item.disabled}
            onClick={(e) => {
              item.onClick?.();
              handleClose();
            }}
            sx={{ gap: 1 }}
          >
            {item.icon && (
              <ListItemIcon
                sx={{ "&.MuiListItemIcon-root": { minWidth: "auto" } }}
              >
                <item.icon sx={{ fontSize: 12 }} />
              </ListItemIcon>
            )}
            <ListItemText slotProps={{ primary: { sx: { fontSize: 12 } } }}>
              {item.label}
            </ListItemText>
          </MenuItem>,
        ])}
      </Menu>
    </>
  );
}
